import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useContext, useEffect } from "react";
import { LandingLanguageContext, ThemeContext } from "../App";

const content = {
  en: {
    title: "Luxury Stays Above Expectations",
    subtitle:
      "Discover refined comfort, curated rooms, and seamless booking in one place.",
    ctaPrimary: "Explore Rooms",
    ctaSecondary: "Book Instantly",
    sectionTitle: "Featured Rooms",
    sectionCopy: "Handpicked rooms ready for your next unforgettable stay.",
    trustLine: "Trusted by travelers seeking calm, style, and reliability.",
  },
  fr: {
    title: "Des Sejours de Luxe au-Dela des Attentes",
    subtitle:
      "Decouvrez un confort raffine, des chambres soigneusement choisies et une reservation fluide.",
    ctaPrimary: "Voir les Chambres",
    ctaSecondary: "Reserver Maintenant",
    sectionTitle: "Chambres en Vedette",
    sectionCopy: "Des chambres selectionnees pour votre prochain sejour inoubliable.",
    trustLine: "Adopte par des voyageurs en quete de calme, style et fiabilite.",
  },
};

export default function Landing() {
  const navigate = useNavigate();
  const languageContext = useContext(LandingLanguageContext);
  const themeContext = useContext(ThemeContext);
  const t = content[languageContext.language];
  const theme = themeContext.theme;

  useEffect(() => {
    document.title = "Franco Hotel | Premium Stays";
  }, []);

  const heroBackground =
    theme === "dark"
      ? "linear-gradient(135deg, #0f172a 0%, #111827 35%, #1e293b 100%)"
      : "linear-gradient(135deg, #f8fafc 0%, #eef2ff 45%, #f1f5f9 100%)";

  const secondaryText = theme === "dark" ? "text-white/80" : "text-slate-700/85";
  const cardBackground = theme === "dark" ? "bg-slate-900" : "bg-white shadow-[0_20px_50px_rgba(15,23,42,0.08)]";

  return (
    <main className={`min-h-[calc(100vh-64px)] ${theme === "dark" ? "bg-slate-950 text-[#f8f4ef]" : "bg-slate-50 text-slate-900"}`}>
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: heroBackground,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-accent-lime-500/20 blur-3xl" />
        <div className="absolute -bottom-14 left-6 h-44 w-44 rounded-full bg-accent-lime-500/20 blur-3xl" />

        <div className="relative mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65 }}
            className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr]"
          >
            <div className="max-w-2xl">
              <div className="mb-4 inline-flex items-center gap-3 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.24em] text-[#d7f29f] shadow-[0_10px_40px_rgba(0,0,0,0.12)]">
                <span className="grid h-5 w-5 place-items-center rounded-full bg-[#d7f29f]/15 text-accent-lime-500">★</span>
                Welcome to Franco Hotel
              </div>

              <h1 className="text-4xl font-black leading-tight tracking-[-0.04em] sm:text-5xl md:text-6xl">
                Luxury Stays
                <span className="block text-accent-lime-500">Above Expectations</span>
              </h1>

              <p className={`mt-6 max-w-xl text-base leading-8 ${secondaryText}`}>
                {t.subtitle}
              </p>
              <p className={`mt-5 text-sm font-semibold ${theme === "dark" ? "text-[#d7f29f]" : "text-accent-lime-700"}`}>
                {t.trustLine}
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-4">
                <button
                  onClick={() => navigate("/booking")}
                  className="rounded-full bg-accent-lime-500 px-8 py-3 text-sm font-bold text-[#2b0818] shadow-[0_20px_60px_rgba(148,215,40,0.2)] transition hover:bg-accent-lime-400"
                >
                  {t.ctaSecondary}
                </button>
                <button
                  onClick={() => {
                    const el = document.getElementById("landing-rooms");
                    if (el) el.scrollIntoView({ behavior: "smooth" });
                  }}
                  className={`rounded-full border px-8 py-3 text-sm font-semibold transition ${theme === "dark" ? "border-white/30 bg-white/10 text-white hover:bg-white/20" : "border-slate-300 bg-white text-slate-900 hover:bg-slate-100"}`}
                >
                  {t.ctaPrimary}
                </button>
              </div>
            </div>

            <div className="relative">
              <div className={`overflow-hidden rounded-[1.75rem] border ${theme === "dark" ? "border-white/10" : "border-slate-200"} ${cardBackground}`}>
                <img
                  src="/og-image.jpg"
                  alt="Franco Hotel"
                  className="h-100 w-full object-cover object-center"
                />
              </div>

              <div className="absolute -bottom-6 left-0 right-0 mx-auto hidden w-fit items-center gap-3 rounded-3xl bg-white px-4 py-3 shadow-2xl shadow-black/20 sm:flex">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-accent-lime-500 text-[#2b0818] text-sm font-bold">
                  ★
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-900">4.8/5</p>
                  <p className="text-[11px] text-slate-500">From 1,200+ happy guests</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
